import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Clock, Shield, Users, Calculator, Calendar } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useCanonical } from '../../../hooks/useCanonical';

const Compliance: React.FC = () => {
  // Add canonical tag for compliance service page
  useCanonical('/services/compliance');
  const complianceServices = [
    {
      title: 'GST Registration',
      description: 'Complete assistance with GST registration and migration for new and existing businesses.',
      icon: FileText,
      features: [
        'New GST Registration',
        'GST Migration Support',
        'Registration Amendments',
        'Cancellation Services'
      ]
    },
    {
      title: 'Return Filing',
      description: 'Timely and accurate filing of all GST returns to ensure compliance.',
      icon: Calculator,
      features: [
        'GSTR-1 (Outward Supplies)',
        'GSTR-3B (Summary Return)',
        'GSTR-9 (Annual Return)',
        'GSTR-9C (Reconciliation)'
      ]
    },
    {
      title: 'Compliance Monitoring',
      description: 'Proactive monitoring and alerts to prevent compliance issues.',
      icon: Shield,
      features: [
        'Due Date Monitoring',
        'Compliance Alerts',
        'Penalty Prevention',
        'Regular Updates'
      ]
    },
    {
      title: 'Documentation Support',
      description: 'Comprehensive documentation support for all GST-related requirements.',
      icon: FileText,
      features: [
        'Invoice Compliance',
        'Document Management',
        'Record Keeping',
        'Audit Support'
      ]
    },
    {
      title: 'Reconciliation Services',
      description: 'Expert reconciliation of books with GST returns for accuracy.',
      icon: Calculator,
      features: [
        'ITC Reconciliation',
        'GSTR-2B Matching',
        'Discrepancy Resolution',
        'Report Generation'
      ]
    }
  ];

  const benefits = [
    {
      title: 'Avoid Penalties',
      description: 'Timely filing prevents late fees and penalties',
      icon: Shield
    },
    {
      title: 'Peace of Mind',
      description: 'Expert handling of all compliance requirements',
      icon: Users
    },
    {
      title: 'Time Savings',
      description: 'Focus on your business while we handle compliance',
      icon: Clock
    },
    {
      title: 'Accuracy',
      description: 'Professional expertise ensures error-free filing',
      icon: CheckCircle
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Assessment',
      description: 'Evaluate your current compliance status and requirements'
    },
    {
      step: '2',
      title: 'Planning',
      description: 'Develop a customized compliance strategy'
    },
    {
      step: '3',
      title: 'Implementation',
      description: 'Execute the compliance plan with precision'
    },
    {
      step: '4',
      title: 'Monitoring',
      description: 'Ongoing monitoring and support'
    }
  ];

  const importantInfo = [
    {
      title: 'Due Dates',
      items: [
        'GSTR-1: 11th of next month',
        'GSTR-3B: 20th of next month',
        'GSTR-9: 31st December',
        'GSTR-9C: 31st December'
      ]
    },
    {
      title: 'Late Fees',
      items: [
        'Daily late fee: ₹50 per day',
        'Maximum late fee: ₹5,000 per return',
        'Interest: 18% per annum',
        'Penalty for non-filing: ₹10,000'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              GST Compliance Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed mb-8">
              Comprehensive GST compliance solutions to ensure your business stays compliant 
              and avoids penalties while optimizing your tax position.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                             <Link
                 to="/login"
                 className="bg-primary hover:bg-slate-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105"
               >
                 Get Started Today
                 <ArrowRight className="ml-2" size={20} />
               </Link>
               <Link
                 to="/contact"
                 className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
               >
                 Free Consultation
               </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Breakdown */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Compliance Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              End-to-end GST compliance support tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {complianceServices.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-200 p-6">
                <div className="flex items-center mb-4">
                  <service.icon className="text-primary mr-3" size={28} />
                  <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Compliance Services?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional expertise and dedicated support for all your GST compliance needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                        <div className="bg-primary-lighter w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <benefit.icon className="text-primary" size={32} />
        </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Compliance Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple, efficient, and transparent process to ensure your compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Important Compliance Information
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay informed about critical deadlines and requirements
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {importantInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                <ul className="space-y-2">
                  {info.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center text-gray-600">
                      <Calendar className="text-primary mr-2 flex-shrink-0" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Ensure Your GST Compliance?
          </h2>
          <p className="text-xl text-primary-lighter mb-8 max-w-2xl mx-auto">
            Let our expert team handle your GST compliance while you focus on growing your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                         <Link
               to="/login"
               className="bg-white text-primary hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
             >
               Get Started Today
             </Link>
             <Link
               to="/contact"
               className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
             >
               Free Consultation
             </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Compliance;
